import { connect } from 'react-redux';
import { Dispatch } from 'redux';

import { Action } from '../../actions';
import {
    phrasesSelectorBulkKeywordsAddMetrica,
    phrasesSelectorKeywordAddMetrica,
    phrasesSelectorManualValueSubmit,
    phrasesSelectorMinusPhraseAddMetrica,
    phrasesSelectorMinusWordAddMetrica,
    phrasesSelectorPhraseStatsFetch,
    phrasesSelectorStartPhrasesClearMetrica,
    phrasesSelectorUpdateStartPhrases
} from '../../actions/phrases-selector';
import {
    phrasesSelectorAddKeywords,
    phrasesSelectorAddMinusPhrases,
    phrasesSelectorChangeManualValue,
    phrasesSelectorToggleExcludePhrases
} from '../../actions/phrases-selector/persistentData';
import { PhrasesSelector, PhrasesSelectorProps } from '../../components/PhrasesSelector/PhrasesSelector';
import { mapPhraseNodeToPhrase } from '../../mappers/phrasesSelectorWizard';
import {
    selectPhrasesSelectorData,
    selectPhrasesSelectorMinusPhrasesEnabled
} from '../../selectors/phrasesSelector/phrasesSelector';
import { removeKeywordsFromStartPhrases } from '../../service/PhrasesSelectorWizard/PhrasesSelectorWizard';
import { VirtualTreeHelpers } from '../../service/VirtualTreeHelpers/VirtualTreeHelpers';
import { GroupId } from '../../typings/groups';
import { PhrasesSelectorPhrase, PhrasesSelectorStartPhraseNode, PhrasesSelectorWizardId } from '../../typings/phrases';
import { State } from '../../typings/state';
import { Omit, TDispatchProps, TStateProps } from '../../typings/utils';

export interface OwnProps {
    wizardId: PhrasesSelectorWizardId;
    groupId: GroupId;
    cls?: string;
}

type StateProps = TStateProps<PhrasesSelectorProps, OwnProps>;
type DispatchProps = Omit<
    TDispatchProps<PhrasesSelectorProps, OwnProps>,
    'onBulkKeywordsAdd' | 'onBulkStartPhrasesRemove'
> & {
    onBulkKeywordsAdd: (phrases: Array<PhrasesSelectorPhrase>) => void;
    onBulkStartPhrasesRemoveMetrica: () => void;
};

type MergedProps = PhrasesSelectorProps;

const mapStateToProps = (state: State, ownProps: OwnProps): StateProps => {
    const { wizardId } = ownProps;
    const [
        {
            startPhrasesError,
            startPhrases,
            groupInfo: { keywords: groupKeywords }
        },
        { keywords, manualValue, isExcludePhrases }
    ] = selectPhrasesSelectorData(state, wizardId);
    const areBulkActionsDisabled = !startPhrases.nested.some(phrase => VirtualTreeHelpers.isAnyChildSelected(phrase));
    const areMinusPhrasesEnabled = selectPhrasesSelectorMinusPhrasesEnabled(state, wizardId);

    return {
        isExcludePhrases,
        areBulkActionsDisabled,
        areMinusPhrasesEnabled,
        startPhrases: startPhrases.nested,
        keywords,
        manualValue,
        startPhrasesError,
        groupKeywords
    };
};

const mapDispatchToProps = (dispatch: Dispatch<Action>, ownProps: OwnProps): DispatchProps => {
    const { groupId, wizardId } = ownProps;

    return {
        onStartPhrasesChange(phrases: Array<PhrasesSelectorStartPhraseNode>) {
            dispatch(phrasesSelectorUpdateStartPhrases(wizardId, phrases));
        },
        onKeywordAdd(phrase: PhrasesSelectorPhrase) {
            dispatch(phrasesSelectorAddKeywords(groupId, wizardId, [phrase]));
            dispatch(phrasesSelectorKeywordAddMetrica(wizardId));
        },
        onMinusPhraseAdd(phrase: string) {
            dispatch(phrasesSelectorAddMinusPhrases(groupId, wizardId, [phrase]));
            dispatch(phrasesSelectorMinusPhraseAddMetrica(wizardId));
        },
        onMinusWordAdd(word: string) {
            dispatch(phrasesSelectorAddMinusPhrases(groupId, wizardId, [word]));
            dispatch(phrasesSelectorMinusWordAddMetrica(wizardId));
        },
        onPhraseStatsFetch(phrase: PhrasesSelectorStartPhraseNode) {
            dispatch(phrasesSelectorPhraseStatsFetch(wizardId, phrase));
        },
        onManualValueChange(value: string) {
            dispatch(phrasesSelectorChangeManualValue(groupId, value));
        },
        onManualValueSubmit() {
            dispatch(phrasesSelectorManualValueSubmit(wizardId));
        },
        onExcludePhrasesToggle() {
            dispatch(phrasesSelectorToggleExcludePhrases(groupId));
        },
        onBulkKeywordsAdd(phrases: Array<PhrasesSelectorPhrase>) {
            dispatch(phrasesSelectorAddKeywords(groupId, wizardId, phrases));
            dispatch(phrasesSelectorBulkKeywordsAddMetrica(wizardId));
        },
        onBulkStartPhrasesRemoveMetrica() {
            dispatch(phrasesSelectorStartPhrasesClearMetrica(wizardId));
        }
    };
};

const mergeProps = (stateProps: StateProps, dispatchProps: DispatchProps, ownProps: OwnProps): MergedProps => {
    return {
        ...stateProps,
        ...dispatchProps,
        ...ownProps,
        onBulkKeywordsAdd() {
            const { startPhrases, keywords } = stateProps;
            const phrasesWithoutKeywords = removeKeywordsFromStartPhrases(startPhrases, keywords);
            const selectedPhrases = VirtualTreeHelpers.filterNodes(phrasesWithoutKeywords, node => node.state.selected);
            const flattenedPhrases = VirtualTreeHelpers.flattenTree(selectedPhrases);

            dispatchProps.onBulkKeywordsAdd(flattenedPhrases.map(phrase => mapPhraseNodeToPhrase(phrase)));
        },
        onBulkStartPhrasesRemove() {
            const { startPhrases } = stateProps;

            const remainingPhrases = VirtualTreeHelpers.filterNodes(startPhrases, node => !node.state.selected);

            dispatchProps.onBulkStartPhrasesRemoveMetrica();
            dispatchProps.onStartPhrasesChange(remainingPhrases);
        }
    };
};

export const PhrasesSelectorContainer = connect<StateProps, DispatchProps, OwnProps, MergedProps>(
    mapStateToProps,
    mapDispatchToProps,
    mergeProps
)(PhrasesSelector);
